import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FunnelHeader, FunnelProgress } from '@/components/health-check/FunnelChrome';
import { SALUTATIONS, US_STATES, loadIntake, saveIntake, captureMeta, submitQualifier, formatPhone } from '@/lib/bhc';
import '@/styles/business-health-check.css';

// [name, question, [ [value,label], ... ], help? ]
const BUSINESS_Q = [
  ['entity_type', 'What type of entity is your business?', [['Sole proprietor'], ['Single-member LLC'], ['Multi-member LLC'], ['S-Corp'], ['Partnership'], ['C-Corp']]],
  ['entities', 'How many business entities do you have?', [['1', 'Just 1'], ['2', '2'], ['3+', '3 or more']]],
  ['states', 'How many states do you operate in?', [['1', '1 state'], ['2', '2 states'], ['3+', '3 or more']]],
  ['bank', 'How many business bank accounts?', [['1', '1'], ['2', '2'], ['3+', '3 or more']], 'The fixed fee covers up to 2 bank accounts.'],
  ['cards', 'How many business credit cards?', [['0–1', '0–1'], ['2', '2'], ['3+', '3 or more']], 'The fixed fee covers up to 2 credit cards.'],
  ['months_behind', 'How many months behind are your books?', [['Current'], ['1–6', '1–6 months'], ['7–12', '7–12 months'], ['12+', '12+ months']]],
  ['software', 'What accounting software do you use?', [['QuickBooks'], ['Xero'], ['Spreadsheets'], ['None'], ['Other']]],
  ['payroll', 'Do you run payroll?', [['No'], ['Yes']]],
  ['sales_tax', 'Do you collect sales tax?', [['No'], ['Yes — 1 state'], ['Yes — multiple', 'Yes — multiple states']]],
  ['prior_returns', 'Are your prior-year tax returns filed?', [['Yes'], ['No'], ['Not sure']]],
  ['dispute', 'Are you in an active dispute with the IRS or a state agency?', [['No'], ['Yes']]],
];

const TRUST_Q = [
  ['t_entity', 'What type of entity is it?', [['Simple trust'], ['Complex trust'], ['Grantor trust'], ["Decedent's estate"]]],
  ['t_1041', 'Is a federal Form 1041 required?', [['Yes'], ['No'], ['Not sure']]],
  ['t_nc', 'NC-source income, or NC resident fiduciary/beneficiary?', [['Yes'], ['No'], ['Not sure']]],
  ['t_income', 'Approximate annual trust/estate income?', [['Under $16k'], ['$16k–$100k'], ['$100k–$500k'], ['$500k+']]],
  ['t_distrib', 'Are distributions being made to beneficiaries?', [['Yes'], ['No']]],
  ['t_dispute', 'Is there an active beneficiary dispute or court proceeding?', [['No'], ['Yes']]],
];

const OptGroup = ({ name, options, value, onChange }) => (
  <div className="optgrid" data-group={name}>
    {(options || []).map(([val, label]) => (
      <label className="opt" key={val}>
        <input type="radio" name={name} value={val} checked={value === val} onChange={() => onChange(name, val)} data-testid={`opt-${name}-${val}`} />
        <span>{label || val}</span>
      </label>
    ))}
  </div>
);

export default function Qualifier() {
  const location = useLocation();
  const navigate = useNavigate();
  const variant = new URLSearchParams(location.search).get('v') === 'trust' ? 'trust' : 'business';
  const isTrust = variant === 'trust';
  const questions = isTrust ? TRUST_Q : BUSINESS_Q;

  const saved = loadIntake();
  const [answers, setAnswers] = useState(() => (saved.variant === variant ? saved : {}));
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const setField = (e) => set(e.target.name, e.target.value);

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = questions.map((q) => q[0]);
    const missing = (required || []).some((n) => !answers[n]);
    const first = (answers.first || '').trim();
    const last = (answers.last || '').trim();
    const email = (answers.email || '').trim();
    const phone = (answers.phone || '').trim();
    if (missing || !first || !last || !/.+@.+\..+/.test(email) || phone.replace(/\D/g, '').length < 7) {
      setError(true);
      document.getElementById('bhc-form-error')?.scrollIntoView({ block: 'center' });
      return;
    }
    setError(false);
    setSubmitting(true);

    const intake = {};
    (required || []).forEach((n) => { intake[n] = answers[n]; });
    const contact = {
      salutation: answers.salutation || '',
      first_name: first,
      last_name: last,
      state: answers.state || '',
      business: isTrust ? '' : (answers.business || ''),
      role: isTrust ? (answers.role || '') : '',
      email,
      phone: answers.phone || '',
      phone_ext: answers.phone_ext || '',
    };

    // persist for funnel continuity (custom-quote prefill / later steps)
    saveIntake({ ...answers, variant, intake, ...contact, name: `${first} ${last}`.trim() });

    try {
      const res = await submitQualifier({ variant, intake, contact, meta: captureMeta() });
      saveIntake({ ...loadIntake(), engagement_id: res.engagement_id, in_scope: res.in_scope, scope_reasons: res.scope_reasons });
      if (res.in_scope) {
        navigate('/business-health-check/terms');
      } else {
        navigate('/business-health-check/custom-quote');
      }
    } catch (err) {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="bhc-root">
      <Helmet>
        <title>See if you qualify · {isTrust ? 'Trust & Estate' : 'Business'} Health Check | KGOB</title>
        <meta name="description" content="See if your business qualifies for the KGOB fixed-fee Business Health Check." />
        <meta name="robots" content="noindex" />
      </Helmet>
      <FunnelHeader back={{ label: 'Back to overview', to: isTrust ? '/business-health-check/trust-estate' : '/business-health-check' }} />
      <FunnelProgress step={1} />

      <main className="funnel">
        <div className="wrap funnel__grid">
          <div>
            <div className="funnel__head">
              <span className="eyebrow">Step 1 · Confirm your fit</span>
              <h1>{isTrust ? 'See if the Fiduciary Health Check fits.' : 'See if the Health Check fits your business.'}</h1>
              <p>{isTrust
                ? 'A few quick questions confirm the trust or estate matches this fixed-fee diagnostic. We identify issues and coordinate with your attorney — we never draft or interpret legal documents.'
                : 'A few quick questions confirm your situation matches this fixed-fee engagement — so when you pay, there are no surprises. Takes about 90 seconds.'}</p>
            </div>

            <form id="qform" onSubmit={onSubmit} noValidate data-testid="qualifier-form">
              <div className="panel">
                <p className="panel__title">{isTrust ? 'The trust or estate' : 'Your situation'}</p>
                <p className="panel__hint">There are no wrong answers — this only helps us route you correctly.</p>
                <div className="fieldset" style={{ marginTop: '24px' }}>
                  {questions.map(([name, q, options, help], i) => (
                    <div className="field" key={name}>
                      <span className="field__q"><span className="num">{String(i + 1).padStart(2, '0')}</span> {q}</span>
                      {help && <span className="field__help">{help}</span>}
                      <OptGroup data-testid="qualifier-optgroup-1" name={name} options={options} value={answers[name]} onChange={set} />
                    </div>
                  ))}
                </div>
              </div>

              {/* CONTACT */}
              <div className="panel">
                <p className="panel__title">Where do we send your confirmation?</p>
                <div className="fieldset" style={{ marginTop: '20px' }}>
                  <div className="row3">
                    <div className="field"><label className="field__q" htmlFor="f_salutation">Title</label>
                      <select data-testid="qualifier-select-3" className="select" id="f_salutation" name="salutation" value={answers.salutation || ''} onChange={setField}>
                        <option value="">—</option>{(SALUTATIONS || []).map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="field"><label className="field__q" htmlFor="f_first">First name <span style={{ color: '#B4472F' }}>*</span></label><input className="input" id="f_first" name="first" autoComplete="given-name" placeholder="First" value={answers.first || ''} onChange={setField} data-testid="q-first" /></div>
                    <div className="field"><label className="field__q" htmlFor="f_last">Last name <span style={{ color: '#B4472F' }}>*</span></label><input className="input" id="f_last" name="last" autoComplete="family-name" placeholder="Last" value={answers.last || ''} onChange={setField} data-testid="q-last" /></div>
                  </div>
                  <div className="row2" style={{ marginTop: '16px' }}>
                    <div className="field"><label className="field__q" htmlFor="f_state">Primary state</label>
                      <select data-testid="qualifier-select-2" className="select" id="f_state" name="state" value={answers.state || ''} onChange={setField}>
                        <option value="">Select…</option>{(US_STATES || []).map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    {isTrust ? (
                      <div className="field"><label className="field__q" htmlFor="f_role">Your role</label>
                        <select data-testid="qualifier-select-1" className="select" id="f_role" name="role" value={answers.role || ''} onChange={setField}>
                          <option value="">Select…</option><option>Trustee</option><option>Executor</option><option>Personal representative</option><option>Beneficiary</option>
                        </select>
                      </div>
                    ) : (
                      <div className="field"><label className="field__q" htmlFor="f_biz">Business name</label><input data-testid="qualifier-input-1" className="input" id="f_biz" name="business" autoComplete="organization" placeholder="Company, LLC" value={answers.business || ''} onChange={setField} /></div>
                    )}
                  </div>
                  <div className="row2" style={{ marginTop: '16px' }}>
                    <div className="field"><label className="field__q" htmlFor="f_email">Email <span style={{ color: '#B4472F' }}>*</span></label><input className="input" id="f_email" name="email" type="email" autoComplete="email" placeholder="you@company.com" value={answers.email || ''} onChange={setField} data-testid="q-email" /></div>
                    <div className="field"><label className="field__q" htmlFor="f_phone">Phone <span style={{ color: '#B4472F' }}>*</span> <span style={{ fontWeight: 400, opacity: 0.65 }}>· ext. optional</span></label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input className="input" id="f_phone" name="phone" type="tel" autoComplete="tel" placeholder="(704) 555-0100" value={answers.phone || ''} onChange={(e) => setField({ target: { name: 'phone', value: formatPhone(e.target.value) } })} data-testid="q-phone" style={{ flex: 1, minWidth: 0 }} />
                        <input className="input" id="f_phone_ext" name="phone_ext" inputMode="numeric" placeholder="Ext." title="Extension (optional — business lines)" value={answers.phone_ext || ''} onChange={(e) => setField({ target: { name: 'phone_ext', value: e.target.value.replace(/\D/g, '').slice(0, 6) } })} data-testid="q-phone-ext" style={{ flex: '0 0 76px', width: '76px' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {error && <div id="bhc-form-error" className="callout callout--warn" style={{ marginTop: '20px' }} data-testid="qualifier-error">Please answer every question and add your name, email, and phone so we can route you correctly.</div>}

              <div className="actionbar">
                <button type="submit" className="btn btn--primary btn--lg" disabled={submitting} data-testid="qualify-submit">{submitting ? 'Checking…' : 'Check my fit'} <span className="arrow">→</span></button>
                <span className="actionbar__hint">No payment yet — this only confirms scope.</span>
              </div>
            </form>
          </div>

          {/* SUMMARY RAIL */}
          <aside className="summary" data-testid="summary">
            <div className="summary__card">
              <h3>{isTrust ? 'Trust & Estate Health Check' : 'Mid-Year Business Health Check'}</h3>
              <div className="summary__line"><span>Fixed-fee diagnostic</span><span>Jan 1 – Jun 30</span></div>
              <div className="summary__line"><span>Deliverables</span><span>{isTrust ? '6 included' : '10 included'}</span></div>
              <div className="summary__line"><span>Turnaround</span><span>15 business days*</span></div>
              <div className="summary__total"><span style={{ fontSize: '13px', color: 'var(--gold-300)', textTransform: 'uppercase', letterSpacing: '.12em' }}>One fixed fee</span><b>$4,995</b></div>
              <ul className="summary__incl">
                <li><span className="tick">✓</span> Bookkeeping catch-up + financials</li>
                <li><span className="tick">✓</span> Tax projection &amp; savings review</li>
                <li><span className="tick">✓</span> Payroll &amp; sales-tax compliance</li>
                <li><span className="tick">✓</span> Written findings + 60-min strategy</li>
              </ul>
              <p className="summary__note">*From receipt of your complete records, not your payment date. Tax figures are estimates, not guarantees.</p>
            </div>
            <div className="callout callout--info" style={{ marginTop: '18px' }}>
              <strong>Not quite a fit?</strong> No problem — if your situation is larger, we'll build a tailored <strong>custom proposal around your requirements</strong> instead. Either way you move forward.
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
